import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail, Lock, User, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { KadmiLogo } from '../components/KadmiLogo';
import { useTranslation } from 'react-i18next';
import BoutonOeil from '../components/ui/BoutonOeil';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import analytics from '../services/analytics';

const V = '#7FB342';

const RegisterPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { register } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [prenom, setPrenom] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptCgu, setAcceptCgu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailAlreadyExists, setEmailAlreadyExists] = useState(false);

  const bg = isDark ? '#0F0F1A' : '#F7F6FF';
  const cardBg = isDark ? '#1A1A2E' : '#FFFFFF';
  const inputBg = isDark ? '#252538' : '#FFFFFF';
  const tp = isDark ? '#F0EFFF' : '#1A1A2E';
  const ts = isDark ? '#9998B8' : '#6B6B8A';
  const bd = isDark ? '#2E2E45' : '#E5E3F0';
  const inputCls = `w-full h-12 pl-10 pr-12 rounded-xl text-sm outline-none transition-colors`;

  const getPasswordStrength = (pw) => {
    if (!pw) return { level: 0, label: '', color: '' };
    if (pw.length < 8) return { level: 1, label: t('auth.password_weak'), color: '#EF4444' };
    const hasNum = /\d/.test(pw);
    const hasSpecial = /[^a-zA-Z0-9]/.test(pw);
    if (hasNum && hasSpecial) return { level: 3, label: t('auth.password_strong'), color: '#22C55E' };
    if (hasNum || hasSpecial) return { level: 2, label: t('auth.password_medium'), color: '#F59E0B' };
    return { level: 1, label: t('auth.password_weak'), color: '#EF4444' };
  };

  const strength = getPasswordStrength(password);
  const passwordsMatch = confirmPassword && password === confirmPassword;
  const passwordsDontMatch = confirmPassword && password !== confirmPassword;
  const isValid = prenom && email && password.length >= 8 && passwordsMatch && acceptCgu;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) return;
    setLoading(true);
    setError('');
    setEmailAlreadyExists(false);
    analytics.signupStarted('register_page');
    try {
      const result = await register('', prenom, email, password);
      analytics.signupCompleted(result?.user?.id || 'unknown');
      toast.success(t('auth.register_success'));
      navigate('/onboarding');
    } catch (err) {
      // Try to surface the real backend error so users know what to fix
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      let msg = '';
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length) {
        msg = detail.map((d) => d.msg || '').join(' · ');
      }

      const lower = (msg || '').toLowerCase();

      // Detect "email already exists" via message OR HTTP 400 (when prod gateway swallows the body)
      const isEmailExists = lower.includes('existe') || lower.includes('exist') || lower.includes('already')
        || (status === 400 && !lower); // 400 sans détail = quasi-toujours email déjà pris (l'API n'a pas d'autre 400 sur register)

      if (isEmailExists) {
        setError(t('auth.email_exists'));
        setEmailAlreadyExists(true); // Affiche le CTA "se connecter / mot de passe oublié"
      } else if (lower.includes('email') && (lower.includes('invalide') || lower.includes('invalid') || lower.includes('format'))) {
        setError(t('auth.invalid_email', 'Adresse email invalide.'));
      } else if (status === 429) {
        setError(t('auth.too_many_attempts', 'Trop de tentatives. Patientez 1 minute et réessayez.'));
      } else if (status >= 500 || status === undefined) {
        setError(t('auth.server_error', 'Le serveur ne répond pas. Vérifiez votre connexion ou réessayez dans quelques instants.'));
      } else if (msg) {
        setError(msg);
      } else {
        setError(t('auth.register_generic_error'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 transition-colors relative" style={{ background: bg }}>
      {/* Back to home arrow */}
      <Link
        to="/"
        data-testid="register-back-link"
        aria-label="Retour à l'accueil"
        className="fixed top-4 left-4 z-10 w-11 h-11 rounded-full flex items-center justify-center transition-all hover:-translate-y-0.5"
        style={{ background: cardBg, color: tp, boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}
      >
        <ArrowLeft className="h-5 w-5" />
      </Link>
      <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-[420px] rounded-[20px] p-8 sm:p-10" style={{ background: cardBg, boxShadow: 'var(--shadow-card)' }}>
        {/* Logo */}
        <div className="text-center mb-6">
          <Link to="/" className="inline-flex items-center gap-2">
            <KadmiLogo size={40} />
          </Link>
          <h1 className="font-heading text-xl mt-4" style={{ color: tp }} data-testid="register-title">{t('auth.register_title')}</h1>
          <p className="text-sm mt-1" style={{ color: ts }}>{t('auth.free_letters_info')}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Prénom */}
          <div>
            <label className="block text-sm font-semibold mb-1.5" style={{ color: tp }}>{t('auth.first_name')}</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: ts }} />
              <input type="text" value={prenom} onChange={e => setPrenom(e.target.value)} placeholder={t('auth.first_name')}
                className={inputCls} style={{ background: inputBg, border: `1px solid ${bd}`, color: tp }} data-testid="register-name-input" />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-semibold mb-1.5" style={{ color: tp }}>{t('auth.email')}</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: ts }} />
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t('auth.email_placeholder')}
                className={inputCls} style={{ background: inputBg, border: `1px solid ${bd}`, color: tp }} data-testid="register-email-input" />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-semibold mb-1.5" style={{ color: tp }}>{t('auth.password')}</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 z-10" style={{ color: ts }} />
              <BoutonOeil value={password} onChange={e => setPassword(e.target.value)} placeholder={t('auth.min_8_chars')}
                className={`${inputCls} pl-10`} style={{ background: inputBg, border: `1px solid ${bd}`, color: tp }} data-testid="register-password-input" />
            </div>
            {password && (
              <div className="mt-1.5 flex items-center gap-2">
                <div className="flex-1 h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${(strength.level / 3) * 100}%`, background: strength.color }} />
                </div>
                <span className="text-xs font-medium" style={{ color: strength.color }}>{strength.label}</span>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-semibold mb-1.5" style={{ color: tp }}>{t('auth.confirm_password')}</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 z-10" style={{ color: ts }} />
              <BoutonOeil value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder={t('auth.confirm_password')}
                className={`${inputCls} pl-10`} style={{ background: inputBg, border: `1px solid ${passwordsMatch ? '#22C55E' : passwordsDontMatch ? '#EF4444' : bd}`, color: tp }} data-testid="register-confirm-input" />
              {passwordsMatch && <CheckCircle className="absolute right-12 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />}
              {passwordsDontMatch && <XCircle className="absolute right-12 top-1/2 -translate-y-1/2 h-4 w-4 text-red-500" />}
            </div>
            {passwordsDontMatch && <p className="text-xs text-red-500 mt-1">{t('auth.passwords_mismatch')}</p>}
          </div>

          {/* CGU */}
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input type="checkbox" checked={acceptCgu} onChange={e => setAcceptCgu(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded accent-[#7FB342]" data-testid="register-cgu-checkbox" />
            <span className="text-xs leading-relaxed" style={{ color: ts }}>
              {t('auth.accept_terms_1')} <Link to="/terms" className="underline" style={{ color: V }}>{t('auth.accept_terms_2')}</Link> {t('auth.accept_terms_3')} <Link to="/privacy" className="underline" style={{ color: V }}>{t('auth.accept_terms_4')}</Link>
            </span>
          </label>

          {/* Error */}
          {error && (
            <div className="text-sm text-red-500 bg-red-50 dark:bg-red-900/20 rounded-lg p-3" data-testid="register-error">
              <p>{error}</p>
              {emailAlreadyExists && (
                <div className="flex flex-col sm:flex-row gap-2 mt-2 pt-2 border-t border-red-200 dark:border-red-900/40">
                  <Link to="/login" className="font-semibold underline" style={{ color: V }} data-testid="register-error-login-link">
                    Se connecter à mon compte
                  </Link>
                  <span className="opacity-50 hidden sm:inline">·</span>
                  <Link to="/forgot-password" className="font-semibold underline" style={{ color: V }} data-testid="register-error-forgot-link">
                    Mot de passe oublié ?
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* Submit */}
          <button type="submit" disabled={!isValid || loading}
            className="w-full h-12 rounded-xl text-white font-semibold text-sm disabled:opacity-40 transition-all"
            style={{ background: V, boxShadow: `0 2px 12px rgba(30,91,224,0.3)` }} data-testid="register-submit-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin inline" /> : t('auth.create_free_account')}
          </button>
        </form>

        {/* Footer */}
        <p className="text-center text-sm mt-6" style={{ color: ts }}>
          {t('auth.have_account')} <Link to="/login" className="font-semibold" style={{ color: V }}>{t('auth.login_button')}</Link>
        </p>
      </motion.div>
    </div>
  );
};

export default RegisterPage;
